import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { BarChart3, ArrowLeft } from "lucide-react";
import { PUBLIC_BUSINESS } from "@/lib/public-business";

export default function PrivacyPage() {
  const { t } = useTranslation(["auth"]);
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg">DealerMind</span>
          </div>
          <Link
            href="/login"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="link-back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("auth:pageChrome.backToLogin")}
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <h1 className="text-3xl font-bold mb-1">Privacy Policy</h1>
          <p className="text-muted-foreground text-sm mb-10">Last updated: August 16, 2026</p>

          <p className="text-muted-foreground leading-relaxed mb-8">
            This Privacy Policy describes how DealerMind Intelligence LLC ("DealerMind," "we," "us," or "our") collects,
            uses, and shares information about you when you use the DealerMind platform ("Service"). By using the
            Service, you agree to the collection and use of information as described in this Policy.
          </p>

          <Section title="1. Information We Collect">
            <Clause title="1.1 Account Information.">
              When you register, we collect your name, company name, email address, and password (stored as a
              salted hash — we never store plaintext passwords).
            </Clause>
            <Clause title="1.2 Financial Documents and Data.">
              You may upload financial documents including lender statements, bank statements, tax returns, profit
              and loss statements, and other business records. The data extracted from these documents is stored
              in your account. Uploaded document files are retained in private, encrypted cloud storage so you can
              view, download, and re-analyze them from your Documents page.
            </Clause>
            <Clause title="1.3 Sensitive Information in Documents.">
              Documents you upload — particularly tax returns — may contain sensitive information such as Social
              Security numbers, Employer Identification Numbers, and bank account numbers. We treat all uploaded
              documents as confidential: they are stored in private storage accessible only to your account (and
              team members you authorize), served only through authenticated, time-limited download links, and are
              never made publicly accessible. We do not use this sensitive information for any purpose other than
              providing the Service to you.
            </Clause>
            <Clause title="1.4 Inventory and Operational Data.">
              We collect vehicle inventory data, lender terms, cost calculations, staffing, marketing spend, and
              other operational data you enter or that is extracted from uploaded documents.
            </Clause>
            <Clause title="1.5 Payment Information.">
              We collect billing and payment information (credit/debit card details) when you subscribe. Payment
              card data is processed and stored by Stripe — we do not store raw card numbers on our servers.
            </Clause>
            <Clause title="1.6 Sales Representative Payout Information.">
              If you are an independent sales representative receiving commission payouts, we collect the bank
              account details (account and routing numbers) you provide for direct deposit. These are stored
              encrypted and shared only with our payment partner (Mercury) to process your payouts.
            </Clause>
            <Clause title="1.7 Usage Data.">
              We automatically collect information about how you interact with the Service, including pages visited,
              features used, and actions taken, along with technical data such as IP address, browser type, and
              operating system. If an application error occurs, technical details about the error (which may include
              your account identifier and the request being made) are captured by our error-monitoring provider to
              help us diagnose and fix problems.
            </Clause>
            <Clause title="1.8 Communications.">
              If you contact us by email or through the platform, we retain records of those communications.
            </Clause>
            <Clause title="1.9 Google Calendar Data.">
              If you connect your Google account to enable scheduling features in DealerMind (such as viewing
              calendar availability), we access your Google Calendar data, specifically calendar events and
              availability, solely to provide those features within the Service. We do not store calendar event
              content beyond what is necessary to fulfill your immediate request, and we do not share this data
              with third parties except as required to operate the feature.
            </Clause>
            <Clause title="1.10 Google API Services — Limited Use.">
              DealerMind's use of information received from Google APIs adheres to the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements. In particular, we use Google user data only to provide
              or improve user-facing features that are prominent in our application's user interface; we do not
              use it to serve advertising, share it with third parties for their independent use, or allow
              humans to read it except with your affirmative agreement or as required for security or legal
              compliance.
            </Clause>
          </Section>

          <Section title="2. How We Use Your Information">
            <p className="text-muted-foreground leading-relaxed mb-3">We use the information we collect to:</p>
            <ul className="space-y-1 list-disc pl-5 text-muted-foreground">
              <li>Provide, maintain, and improve the Service</li>
              <li>Process your subscription and billing</li>
              <li>Authenticate your identity and secure your account</li>
              <li>Power AI-assisted features, including document extraction and analytical insights</li>
              <li>Send you transactional emails (account alerts, payment receipts, password resets)</li>
              <li>Respond to your support requests</li>
              <li>Detect, prevent, and investigate fraud, abuse, or security incidents</li>
              <li>Comply with applicable legal obligations</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We do not sell your personal information to third parties. We do not use your financial documents
              or uploaded data to train AI models for purposes other than providing the Service to you.
            </p>
            <Clause title="AI Processing.">
              When you upload a document or use an AI feature (such as document extraction, financial analysis,
              chat assistance, or automated assessments), the relevant document content or data is transmitted
              to our AI providers (Anthropic and OpenAI) for processing. Under our agreements with these
              providers, your data is used only to generate the requested output and is not used to train their
              models. AI-generated results are estimates and are stored in your account alongside the source data.
            </Clause>
            <Clause title="Text messaging (SMS).">
              For lead and account alerts requested by dealership staff who opt in inside DealerHub
              (Settings → Notifications), we collect the mobile number, consent choice, consent time,
              and consent source. This information
              is used only to send the requested alerts. Reply STOP to opt out at any time. Mobile numbers and
              SMS consent information will not be shared with third parties or affiliates for marketing or
              promotional purposes. See our <Link href="/sms-alerts" className="underline">SMS Alerts</Link>.
            </Clause>
          </Section>

          <Section title="3. Third-Party Service Providers">
            <p className="text-muted-foreground leading-relaxed mb-4">
              We share information with trusted third-party providers who help us operate the Service. These
              providers are contractually required to protect your information and may only use it to provide
              services on our behalf.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse" data-testid="table-third-parties">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-semibold text-foreground">Provider</th>
                    <th className="text-left py-2 pr-4 font-semibold text-foreground">Purpose</th>
                    <th className="text-left py-2 font-semibold text-foreground">Data Shared</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground divide-y">
                  {[
                    { provider: "Google", purpose: "Google Calendar integration — reading calendar events and availability to support scheduling features", data: "Google Calendar events and availability (read-only, only when connected)" },
                    { provider: "Stripe", purpose: "Payment processing and subscription management", data: "Billing info, email, name" },
                    { provider: "OpenAI", purpose: "AI chat assistant, financial analysis, and validation features", data: "User prompts, document metadata, business data you provide" },
                    { provider: "Anthropic", purpose: "AI document extraction (lender statements, bank statements, tax returns) and assessments", data: "Content of uploaded documents, business data" },
                    { provider: "Mercury", purpose: "Commission payouts to sales representatives", data: "Payout amounts, recipient bank details" },
                    { provider: "Neon", purpose: "Cloud PostgreSQL database hosting", data: "All stored account and business data" },
                    { provider: "Google Cloud Storage", purpose: "Private encrypted storage of uploaded document files", data: "Uploaded document files" },
                    { provider: "Replit", purpose: "Application hosting and infrastructure", data: "All application data" },
                    { provider: "Resend", purpose: "Transactional and alert email delivery", data: "Email address, name, alert content" },
                    { provider: "Twilio", purpose: "SMS delivery", data: "Mobile number, message content" },
                    { provider: "Sentry", purpose: "Error monitoring and diagnostics", data: "Error details, technical request context, account identifiers" },
                  ].map((row) => (
                    <tr key={row.provider}>
                      <td className="py-2.5 pr-4 font-medium text-foreground">{row.provider}</td>
                      <td className="py-2.5 pr-4">{row.purpose}</td>
                      <td className="py-2.5">{row.data}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We may also disclose information to law enforcement or government authorities when required by law
              or to protect the rights and safety of DealerMind or others.
            </p>
          </Section>

          <Section title="4. Data Retention and Storage Location">
            <p className="text-muted-foreground leading-relaxed mb-3">
              Your data is stored and processed in the United States. If you access the Service from outside the
              United States, you understand that your information will be transferred to and stored in the United
              States.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              We retain your account information and uploaded data for as long as your account is active. After
              account termination, we retain your data for 30 days to allow for export requests, after which it
              is permanently deleted from our systems. Billing records and transactional logs may be retained for
              up to 7 years as required by financial regulations.
            </p>
          </Section>

          <Section title="5. Data Security">
            <p className="text-muted-foreground leading-relaxed mb-3">
              We implement industry-standard security measures including:
            </p>
            <ul className="space-y-1 list-disc pl-5 text-muted-foreground">
              <li>HTTPS/TLS encryption for all data in transit</li>
              <li>Encryption at rest for the database and document storage</li>
              <li>Private (non-public) document storage with authenticated, expiring download links</li>
              <li>Per-account access controls — your documents and data are accessible only to your account and team members you authorize</li>
              <li>Encrypted storage for sensitive credentials (e.g., bank account details)</li>
              <li>Salted password hashing using PBKDF2</li>
              <li>Access controls limiting employee access to customer data</li>
              <li>Session management with secure, HttpOnly cookies</li>
              <li>Rate limiting and continuous error monitoring</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              No security measure is perfect or impenetrable. If you believe your account has been compromised,
              contact us immediately at {PUBLIC_BUSINESS.email}.
            </p>
            <Clause title="Breach Notification.">
              If we become aware of a security breach affecting your personal information, we will notify you
              without undue delay, consistent with applicable law, and will describe the nature of the breach
              and the steps we are taking in response.
            </Clause>
          </Section>

          <Section title="6. Your Rights and Choices">
            <Clause title="6.1 Access and Correction.">
              You can access and update your account information through the account settings page.
            </Clause>
            <Clause title="6.2 Data Export.">
              You may request an export of Your Data by contacting {PUBLIC_BUSINESS.email}.
            </Clause>
            <Clause title="6.3 Deletion.">
              You may request deletion of your account and associated data by contacting {PUBLIC_BUSINESS.email}. We will
              process deletion requests within 30 days, subject to any legal retention obligations.
            </Clause>
            <Clause title="6.4 California Residents (CCPA).">
              If you are a California resident, you have the right to: (a) know what personal information we
              collect and how we use it; (b) request deletion of your personal information; (c) opt out of the
              sale of your personal information (we do not sell personal information); and (d) non-discrimination
              for exercising your privacy rights. To exercise these rights, contact {PUBLIC_BUSINESS.email}.
            </Clause>
            <Clause title="6.5 Other U.S. State Privacy Rights.">
              Residents of states with comprehensive privacy laws (including Virginia, Colorado, Connecticut,
              Texas, and Oregon) may have similar rights to access, correct, delete, and obtain a copy of their
              personal information, and to opt out of targeted advertising and profiling (we do not engage in
              either). To exercise any of these rights, contact {PUBLIC_BUSINESS.email}. If we decline a
              request, you may appeal by replying to our decision.
            </Clause>
            <Clause title="6.6 Marketing Communications.">
              We may send you product updates and service announcements. You may opt out of non-transactional
              emails by clicking "Unsubscribe" in any marketing email or contacting {PUBLIC_BUSINESS.email}. Transactional
              emails (receipts, alerts, password resets) cannot be opted out of while your account is active.
            </Clause>
            <Clause title="6.7 Disconnecting Your Google Account.">
              If you have connected your Google account to use the Google Calendar integration, you may
              disconnect it at any time from your account settings in DealerMind. You can also revoke
              DealerMind's access to your Google account at any time through your{" "}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                Google Account security settings
              </a>
              . Revoking access will immediately prevent DealerMind from accessing any further Google Calendar
              data; any data already retrieved and stored will be deleted upon your request.
            </Clause>
          </Section>

          <Section title="7. Cookies and Tracking">
            <p className="text-muted-foreground leading-relaxed">
              We use session cookies to maintain your login state. We do not use third-party advertising cookies
              or cross-site tracking technologies. You may configure your browser to refuse cookies, but doing so
              may prevent certain features of the Service from working correctly.
            </p>
          </Section>

          <Section title="8. Children's Privacy">
            <p className="text-muted-foreground leading-relaxed">
              The Service is not directed to individuals under the age of 18. We do not knowingly collect personal
              information from minors. If we become aware that a minor has provided personal information, we will
              delete it promptly.
            </p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of material changes by email
              at least 14 days before they take effect. Your continued use of the Service after the effective date
              constitutes acceptance of the revised Policy.
            </p>
          </Section>

          <Section title="10. Contact Us">
            <p className="text-muted-foreground leading-relaxed">
              If you have questions about this Privacy Policy or your data, please contact us:
            </p>
            <div className="mt-3 p-4 rounded-lg bg-muted/50 border text-sm text-muted-foreground space-y-1">
              <p><span className="font-medium text-foreground">DealerMind Intelligence LLC</span></p>
              <p>Email: {PUBLIC_BUSINESS.email}</p>
              <p>Mailing Address: {PUBLIC_BUSINESS.mailingAddress}</p>
            </div>
          </Section>
        </div>
      </main>

      <footer className="border-t mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>&copy; 2026 DealerMind. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <Link href="/terms" className="hover:text-foreground transition-colors" data-testid="footer-link-terms">
              Terms of Service
            </Link>
            <Link href="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-link-privacy">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10">
      <h2 className="text-xl font-semibold text-foreground mb-4 pb-2 border-b">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Clause({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-foreground font-medium mb-1">{title}</p>
      <div className="text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}
